'use client';

import { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  TextField,
  Stack,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import Image from 'next/image';

interface ExternalLinksProps {
  links: {
    makerworld?: string | null;
    printables?: string | null;
    thingiverse?: string | null;
    github?: string | null;
  };
  isOwnProfile: boolean;
  userId: string;
}

const SERVICES = {
  makerworld: {
    name: 'Makerworld',
    icon: '/icons/makerworld.svg',
    baseUrl: 'https://makerworld.com/en/@',
  },
  printables: {
    name: 'Printables',
    icon: '/icons/printables.svg',
    baseUrl: 'https://www.printables.com/',
  },
  thingiverse: {
    name: 'Thingiverse',
    icon: '/icons/thingiverse.svg',
    baseUrl: 'https://www.thingiverse.com/',
  },
  github: {
    name: 'GitHub',
    icon: '/icons/github.svg',
    baseUrl: 'https://github.com/',
  },
} as const;

export default function ExternalLinks({ links, isOwnProfile }: ExternalLinksProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedLinks, setEditedLinks] = useState(links);

  const handleSave = async () => {
    try {
      const response = await fetch(`/api/user/external-links`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editedLinks),
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to update links');

      setIsEditing(false);
    } catch (error) {
      console.error('Error updating external links:', error);
      alert('Failed to update external links. Please try again.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2">
          External Links
        </Typography>
        {isOwnProfile && !isEditing && (
          <IconButton size="small" onClick={() => setIsEditing(true)} sx={{ ml: 1 }}>
            <EditIcon />
          </IconButton>
        )}
      </Box>

      {isEditing ? (
        <Stack spacing={2}>
          {Object.entries(SERVICES).map(([key, service]) => (
            <TextField
              key={key}
              fullWidth
              label={service.name}
              placeholder={`Your ${service.name} username`}
              value={editedLinks[key as keyof typeof links] || ''}
              onChange={e =>
                setEditedLinks(prev => ({
                  ...prev,
                  [key]: e.target.value,
                }))
              }
              InputProps={{
                startAdornment: (
                  <Box component="span" sx={{ mr: 1, display: 'flex' }}>
                    <Image src={service.icon} alt={service.name} width={20} height={20} />
                  </Box>
                ),
              }}
            />
          ))}

          <Stack direction="row" spacing={1}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
              Save
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => {
                setEditedLinks(links);
                setIsEditing(false);
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      ) : (
        <List disablePadding>
          {Object.entries(SERVICES).map(([key, service]) => {
            const username = editedLinks[key as keyof typeof links];
            if (!username) return null;

            return (
              <ListItem
                key={key}
                disablePadding
                sx={{ mb: 1 }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    href={service.baseUrl + username}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LaunchIcon />
                  </IconButton>
                }
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Image src={service.icon} alt={service.name} width={20} height={20} />
                </ListItemIcon>
                <ListItemText
                  primary={service.name}
                  secondary={username}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            );
          })}

          {Object.values(editedLinks).every(link => !link) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {isOwnProfile ? 'Add your external profile links' : 'No external links provided'}
            </Typography>
          )}
        </List>
      )}
    </Box>
  );
}
